import jsPDF from "jspdf";

// Colors matching the app theme
const COLORS = {
  primary: [139, 92, 246], // violet-500
  secondary: [113, 113, 122], // zinc-500
  dark: [39, 39, 42], // zinc-800
  light: [244, 244, 245], // zinc-100
  success: [34, 197, 94], // green-500
  warning: [245, 158, 11], // amber-500
  danger: [239, 68, 68], // red-500
  white: [255, 255, 255],
};

export function generateReportPDF(report) {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  // Helper functions
  const addPage = () => {
    doc.addPage();
    yPos = margin;
  };

  const checkPageBreak = (height) => {
    if (yPos + height > pageHeight - margin) {
      addPage();
      return true;
    }
    return false;
  };

  const drawLine = (y, color = COLORS.light) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);
  };

  // Header with logo placeholder
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 40, "F");
  
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("EPR/PPR Compliance", margin, 20);
  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.text("Analysis Report", margin, 28);
  
  // Report metadata
  doc.setFontSize(9);
  doc.text(`Generated: ${new Date(report.generated_at).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  })}`, pageWidth - margin, 20, { align: "right" });
  doc.text(`Platforms: ${report.platforms.join(", ")}`, pageWidth - margin, 28, { align: "right" });

  yPos = 50;

  // Executive Summary
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Executive Summary", margin, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLORS.secondary);
  
  const summaryLines = doc.splitTextToSize(report.executive_summary || "", contentWidth);
  doc.text(summaryLines, margin, yPos);
  yPos += summaryLines.length * 5 + 10;

  drawLine(yPos);
  yPos += 10;

  // Platform Findings
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Platform Findings", margin, yPos);
  yPos += 10;

  if (report.findings && report.findings.length > 0) {
    report.findings.forEach((finding, index) => {
      checkPageBreak(50);

      // Platform header
      doc.setFillColor(...COLORS.light);
      doc.roundedRect(margin, yPos - 4, contentWidth, 35, 2, 2, "F");
      
      doc.setTextColor(...COLORS.dark);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(finding.platform, margin + 5, yPos + 2);
      
      yPos += 10;
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...COLORS.secondary);

      // Stats grid
      const col1 = margin + 5;
      const col2 = margin + contentWidth / 2;
      
      doc.text(`Markets: ${finding.markets?.join(", ") || "N/A"}`, col1, yPos);
      doc.text(`In-House Capabilities: ${finding.in_house_capabilities || 0}`, col2, yPos);
      yPos += 6;
      
      doc.setTextColor(...COLORS.primary);
      doc.text(`Gaps Identified: ${finding.gaps_identified || 0}`, col1, yPos);
      doc.text(`Full Outsource Opportunities: ${finding.full_outsource_opportunities || 0}`, col2, yPos);
      yPos += 8;

      // Key gaps
      if (finding.key_gaps && finding.key_gaps.length > 0) {
        doc.setTextColor(...COLORS.secondary);
        doc.setFontSize(8);
        doc.text("Key Gaps: " + finding.key_gaps.map(g => g.replace(/_/g, " ")).join(", "), col1, yPos);
      }
      
      yPos += 15;
    });
  }

  yPos += 5;
  drawLine(yPos);
  yPos += 10;

  // Recommendations
  if (report.recommendations && report.recommendations.length > 0) {
    checkPageBreak(30);
    
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Recommendations", margin, yPos);
    yPos += 10;

    report.recommendations.forEach((rec, index) => {
      checkPageBreak(25);

      // Priority badge color
      let priorityColor = COLORS.primary;
      if (rec.priority === "P0") priorityColor = COLORS.danger;
      else if (rec.priority === "P1") priorityColor = COLORS.warning;
      
      // Priority badge
      doc.setFillColor(...priorityColor);
      doc.roundedRect(margin, yPos - 3, 12, 5, 1, 1, "F");
      doc.setTextColor(...COLORS.white);
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.text(rec.priority, margin + 6, yPos, { align: "center" });

      // Title
      doc.setTextColor(...COLORS.dark);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(rec.title, margin + 16, yPos);
      yPos += 5;

      // Rationale
      doc.setTextColor(...COLORS.secondary);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      const rationaleLines = doc.splitTextToSize(rec.rationale || "", contentWidth - 16);
      doc.text(rationaleLines, margin + 16, yPos);
      yPos += rationaleLines.length * 4;

      // Impact
      doc.setTextColor(...COLORS.primary);
      doc.setFontSize(8);
      doc.text(`Impact: ${rec.estimated_impact || "N/A"}`, margin + 16, yPos);
      yPos += 10;
    });
  }

  // Service Matrix
  if (report.service_matrix && report.service_matrix.length > 0) {
    checkPageBreak(60);
    
    yPos += 5;
    drawLine(yPos);
    yPos += 10;

    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Service Opportunity Matrix", margin, yPos);
    yPos += 10;

    // Table header
    const colWidth = contentWidth / 4;
    doc.setFillColor(...COLORS.dark);
    doc.rect(margin, yPos - 4, contentWidth, 8, "F");
    
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Service", margin + 5, yPos);
    doc.text("Amazon", margin + colWidth + 5, yPos);
    doc.text("Temu", margin + colWidth * 2 + 5, yPos);
    doc.text("GPAIS", margin + colWidth * 3 + 5, yPos);
    yPos += 8;

    // Table rows
    doc.setFont("helvetica", "normal");
    report.service_matrix.forEach((row, index) => {
      checkPageBreak(8);
      
      if (index % 2 === 0) {
        doc.setFillColor(...COLORS.light);
        doc.rect(margin, yPos - 4, contentWidth, 7, "F");
      }

      doc.setTextColor(...COLORS.dark);
      doc.setFontSize(8);
      doc.text(row.service || "", margin + 5, yPos, { maxWidth: colWidth - 10 });
      
      // Color code the opportunities
      const getStatusColor = (status) => {
        if (status?.includes("Full")) return COLORS.success;
        if (status?.includes("Partial")) return COLORS.warning;
        return COLORS.secondary;
      };

      doc.setTextColor(...getStatusColor(row.amazon));
      doc.text(row.amazon || "N/A", margin + colWidth + 5, yPos);
      
      doc.setTextColor(...getStatusColor(row.temu));
      doc.text(row.temu || "N/A", margin + colWidth * 2 + 5, yPos);
      
      doc.setTextColor(...getStatusColor(row.gpais));
      doc.text(row.gpais || "N/A", margin + colWidth * 3 + 5, yPos);
      
      yPos += 7;
    });
  }

  // Footer on all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.secondary);
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: "center" }
    );
    doc.text(
      "Mind Your Waste - EPR/PPR Compliance Hub",
      margin,
      pageHeight - 10
    );
    doc.text(
      new Date().toLocaleDateString(),
      pageWidth - margin,
      pageHeight - 10,
      { align: "right" }
    );
  }

  return doc;
}

export function downloadReportPDF(report) {
  const doc = generateReportPDF(report);
  const filename = `compliance-report-${new Date().toISOString().split("T")[0]}.pdf`;
  doc.save(filename);
  return filename;
}

export function generateGapAnalysisPDF(analysis) {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  const checkPageBreak = (height) => {
    if (yPos + height > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 40, "F");
  
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("Gap Analysis Report", margin, 20);
  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.text(analysis.platform, margin, 28);
  
  doc.setFontSize(9);
  doc.text(`Generated: ${new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  })}`, pageWidth - margin, 24, { align: "right" });

  yPos = 55;

  // Summary Stats
  const inHousePercentage = Math.round((analysis.in_house_count / analysis.total_categories) * 100);
  const gapPercentage = Math.round((analysis.gap_count / analysis.total_categories) * 100);

  // Stats boxes
  const boxWidth = (contentWidth - 10) / 3;
  
  // Total Categories
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(margin, yPos, boxWidth, 25, 2, 2, "F");
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(9);
  doc.text("Total Categories", margin + 5, yPos + 8);
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(String(analysis.total_categories), margin + 5, yPos + 18);

  // In-House
  doc.setFillColor(220, 252, 231); // green-100
  doc.roundedRect(margin + boxWidth + 5, yPos, boxWidth, 25, 2, 2, "F");
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("In-House", margin + boxWidth + 10, yPos + 8);
  doc.setTextColor(...COLORS.success);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(`${analysis.in_house_count} (${inHousePercentage}%)`, margin + boxWidth + 10, yPos + 18);

  // Gaps
  doc.setFillColor(237, 233, 254); // violet-100
  doc.roundedRect(margin + (boxWidth + 5) * 2, yPos, boxWidth, 25, 2, 2, "F");
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("Service Gaps", margin + (boxWidth + 5) * 2 + 5, yPos + 8);
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(`${analysis.gap_count} (${gapPercentage}%)`, margin + (boxWidth + 5) * 2 + 5, yPos + 18);

  yPos += 35;

  // Service Opportunities Table
  if (analysis.service_opportunities && analysis.service_opportunities.length > 0) {
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Service Opportunities", margin, yPos);
    yPos += 10;

    // Table header
    const colWidths = [45, 55, 25, 25, 25];
    doc.setFillColor(...COLORS.dark);
    doc.rect(margin, yPos - 4, contentWidth, 8, "F");
    
    doc.setTextColor(...COLORS.white);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    let xPos = margin + 3;
    ["Category", "Description", "Complexity", "Opportunity", "Revenue"].forEach((header, i) => {
      doc.text(header, xPos, yPos);
      xPos += colWidths[i];
    });
    yPos += 8;

    // Table rows
    doc.setFont("helvetica", "normal");
    analysis.service_opportunities.forEach((opp, index) => {
      checkPageBreak(10);
      
      if (index % 2 === 0) {
        doc.setFillColor(...COLORS.light);
        doc.rect(margin, yPos - 4, contentWidth, 8, "F");
      }

      doc.setTextColor(...COLORS.dark);
      doc.setFontSize(7);
      
      xPos = margin + 3;
      const category = opp.category.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
      doc.text(category.substring(0, 25), xPos, yPos);
      xPos += colWidths[0];
      
      doc.text((opp.description || "").substring(0, 35), xPos, yPos);
      xPos += colWidths[1];

      // Complexity
      doc.setTextColor(opp.complexity === "high" ? COLORS.danger : opp.complexity === "medium" ? COLORS.warning : COLORS.success);
      doc.text(opp.complexity || "N/A", xPos, yPos);
      xPos += colWidths[2];

      // Opportunity level
      doc.setTextColor(opp.opportunity_level === "full" ? COLORS.primary : COLORS.secondary);
      doc.text(opp.opportunity_level || "N/A", xPos, yPos);
      xPos += colWidths[3];

      // Revenue potential
      doc.setTextColor(opp.revenue_potential === "high" ? COLORS.success : COLORS.secondary);
      doc.text(opp.revenue_potential || "N/A", xPos, yPos);
      
      yPos += 8;
    });
  }

  yPos += 10;

  // Risk Areas
  if (analysis.risk_areas && analysis.risk_areas.length > 0) {
    checkPageBreak(30);
    
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Risk Areas", margin, yPos);
    yPos += 8;

    analysis.risk_areas.forEach((risk) => {
      checkPageBreak(15);
      
      doc.setFillColor(254, 243, 199); // amber-100
      doc.roundedRect(margin, yPos - 3, contentWidth, 12, 2, 2, "F");
      
      doc.setTextColor(...COLORS.warning);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(risk.category?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "", margin + 5, yPos + 2);
      
      doc.setTextColor(...COLORS.secondary);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(risk.description || "", margin + 5, yPos + 7);
      
      yPos += 15;
    });
  }

  // Footer
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.secondary);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: "center" });
    doc.text("Mind Your Waste - EPR/PPR Compliance Hub", margin, pageHeight - 10);
  }

  return doc;
}

export function downloadGapAnalysisPDF(analysis) {
  const doc = generateGapAnalysisPDF(analysis);
  const filename = `gap-analysis-${analysis.platform?.toLowerCase().replace(/\s+/g, "-") || "report"}-${new Date().toISOString().split("T")[0]}.pdf`;
  doc.save(filename);
  return filename;
}
